/**
 * Buttons and links that look like buttons.
 *
 * `variant` picks the shape from kit.css; nothing here carries its own colour.
 * The primary is black rather than brand-green on purpose — the reference
 * spends its accent on the logo alone, and a green button next to a green
 * "live" dot makes the dot stop meaning anything.
 */
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { Link, type LinkProps } from "react-router-dom";

export type Variant = "primary" | "secondary" | "ghost" | "quiet" | "danger";
export type Size = "sm" | "md" | "lg";

const cx = (...parts: unknown[]) => parts.filter((p): p is string => typeof p === "string" && p.length > 0).join(" ");

const classes = (variant: Variant, size: Size, extra?: string) =>
  cx("btn", `btn-${variant}`, size !== "md" && `btn-${size}`, extra);

interface Common {
  variant?: Variant;
  size?: Size;
  children?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, Common & ButtonHTMLAttributes<HTMLButtonElement>>(
  function Button({ variant = "secondary", size = "md", className, type = "button", ...rest }, ref) {
    return <button ref={ref} type={type} className={classes(variant, size, className)} {...rest} />;
  }
);

/** Same shapes, but it navigates. Used wherever the action has an address. */
export function ButtonLink({ variant = "secondary", size = "md", className, ...rest }: Common & LinkProps) {
  return <Link className={classes(variant, size, className)} {...rest} />;
}

/**
 * A round, label-less button. `label` is required and becomes the accessible
 * name — the old top bars shipped three bare icon buttons that read as
 * "button, button, button" to a screen reader.
 */
export function IconButton({
  label,
  size = "md",
  className,
  children,
  ...rest
}: { label: string } & Omit<Common, "variant"> & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={cx("btn", "btn-icon", size === "sm" && "btn-sm", className)}
      {...rest}
    >
      {children}
    </button>
  );
}

export { cx };
